import React, { useEffect, useMemo, useState } from "react";
import { useApp } from "../contexts/AppContext";
import api from "../lib/api";
import { Newspaper, Calendar, ArrowRight, X } from "lucide-react";

const CATEGORY_COLOR = {
    "Announcement": "bg-[hsl(var(--royal))]/10 text-[hsl(var(--royal))]",
    "Update": "bg-emerald-500/10 text-emerald-600",
    "MeeSeva": "bg-amber-500/10 text-amber-600",
    "Alert": "bg-red-500/10 text-red-600",
};

const News = () => {
    const { t } = useApp();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState(null);
    const [cat, setCat] = useState("All");

    useEffect(() => {
        api.get("/news").then(({ data }) => setItems(data)).finally(() => setLoading(false));
    }, []);

    const categories = useMemo(() => ["All", ...Array.from(new Set(items.map((i) => i.category)))], [items]);
    const filtered = useMemo(() => cat === "All" ? items : items.filter((i) => i.category === cat), [items, cat]);

    return (
        <div data-testid="page-news" className="container-x section-pad">
            <div className="max-w-3xl">
                <div className="chip bg-[hsl(var(--gold))]/15 text-[hsl(var(--royal-deep))] dark:text-[hsl(var(--gold-light))] mb-4">
                    <Newspaper size={14} /> {t.news.chip}
                </div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight">{t.news.title}</h1>
                <p className="mt-3 text-muted-foreground">{t.news.desc}</p>
            </div>

            {items.length > 0 && (
                <div className="mt-8 flex flex-wrap gap-2">
                    {categories.map((c) => (
                        <button key={c} data-testid={`news-cat-${c}`} onClick={() => setCat(c)}
                            className={`px-4 h-9 rounded-full text-xs font-semibold whitespace-nowrap transition-colors border ${
                                cat === c ? "bg-[hsl(var(--royal))] text-white border-[hsl(var(--royal))]" : "bg-background border-border hover:bg-muted"
                            }`}>{c}</button>
                    ))}
                </div>
            )}

            <div className="mt-10">
                {loading ? (
                    <div className="py-20 text-center text-muted-foreground">Loading…</div>
                ) : filtered.length === 0 ? (
                    <div className="py-20 text-center text-muted-foreground">{t.news.empty}</div>
                ) : (
                    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {filtered.map((n) => (
                            <article key={n.id} data-testid={`news-card-${n.id}`}
                                className="group rounded-3xl border border-border bg-card overflow-hidden hover:-translate-y-1 hover:shadow-xl transition-transform duration-200 cursor-pointer"
                                onClick={() => setActive(n)}>
                                {n.image_url && (
                                    <div className="aspect-[16/10] overflow-hidden bg-muted">
                                        <img src={n.image_url} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                    </div>
                                )}
                                <div className="p-6">
                                    <div className="flex items-center gap-3 text-xs">
                                        <span className={`px-2.5 py-1 rounded-full font-semibold ${CATEGORY_COLOR[n.category] || "bg-muted"}`}>{n.category}</span>
                                        <span className="text-muted-foreground inline-flex items-center gap-1"><Calendar size={12} /> {new Date(n.published_at).toLocaleDateString()}</span>
                                    </div>
                                    <h3 className="mt-3 text-lg font-semibold tracking-tight leading-snug line-clamp-2">{n.title}</h3>
                                    <p className="mt-2 text-sm text-muted-foreground line-clamp-3">{n.summary}</p>
                                    <div className="mt-4 text-sm font-semibold text-[hsl(var(--royal))] inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                                        {t.news.readMore} <ArrowRight size={14} />
                                    </div>
                                </div>
                            </article>
                        ))}
                    </div>
                )}
            </div>

            {active && (
                <div data-testid="news-modal" onClick={() => setActive(null)}
                    className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-black/60 backdrop-blur-sm p-3 sm:p-6">
                    <div onClick={(e) => e.stopPropagation()} className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-card border border-border shadow-2xl">
                        <button onClick={() => setActive(null)} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-background/80 backdrop-blur flex items-center justify-center hover:bg-muted z-10" aria-label="Close">
                            <X size={18} />
                        </button>
                        {active.image_url && <img src={active.image_url} alt="" className="w-full h-56 sm:h-72 object-cover" />}
                        <div className="p-6 sm:p-8">
                            <div className="flex items-center gap-3 text-xs">
                                <span className={`px-2.5 py-1 rounded-full font-semibold ${CATEGORY_COLOR[active.category] || "bg-muted"}`}>{active.category}</span>
                                <span className="text-muted-foreground inline-flex items-center gap-1"><Calendar size={12} /> {new Date(active.published_at).toLocaleDateString()}</span>
                            </div>
                            <h2 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight leading-tight">{active.title}</h2>
                            {active.summary && <p className="mt-4 text-base text-muted-foreground leading-relaxed">{active.summary}</p>}
                            <div className="mt-4 text-sm leading-relaxed whitespace-pre-line">{active.body}</div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default News;
